import { ReactNode } from 'react';
import { InvestmentRoundingSetting, ReverseVestingSettings } from 'shared';

import { AppFormattedMessage } from '../components/app-formatted-message/index';
import { DividendFrequency, DividendPayoutStatus } from '../hooks/dividend/dividend.types';
import { StringKey } from '../lang/index';
import { Pool, SharePlan } from './pool-plans.types';
import { Safe, SafeWithPermissions } from './safes.types';
import { ShareClass } from './share-classes.types';
import { Stakeholder } from './stakeholder.types';
import { VestingTask } from './vesting-tasks.types';

export enum SortByType {
  APPROVAL = 'approval',
  BUYBACK = 'buyback',
  COMPANY_INCORPORATION = 'company-incorporation',
  CONVERSION = 'conversion',
  DIVIDEND = 'dividend',
  EXERCISE = 'exercise',
  EXPIRE = 'expire',
  FUNDRAISING_ROUND = 'fundraising-round',
  GRANT = 'grant',
  NEW_PLAN = 'new-plan',
  NEW_POOL = 'new-pool',
  PAYOUT = 'payout',
  SAFE = 'safe',
  SAFE_CONVERSION = 'safe-conversion',
  SECONDARIES = 'secondaries',
  SHARE_ISSUANCE = 'share-issuance',
  VALUATION = 'valuation',
}

export const sortByTypeTitle: Record<SortByType, StringKey> = {
  [SortByType.COMPANY_INCORPORATION]: 'INCORPORATION',
  [SortByType.VALUATION]: 'VALUATIONS',
  [SortByType.FUNDRAISING_ROUND]: 'FUNDRAISING',
  [SortByType.GRANT]: 'GRANTS',
  [SortByType.EXERCISE]: 'EXERCISES',
  [SortByType.EXPIRE]: 'EXPIRED',
  [SortByType.DIVIDEND]: 'DIVIDENDS',
  [SortByType.PAYOUT]: 'PAYOUTS',
  [SortByType.APPROVAL]: 'APPROVAL',
  [SortByType.SHARE_ISSUANCE]: 'ISSUANCES',
  [SortByType.BUYBACK]: 'BUYBACKS',
  [SortByType.SECONDARIES]: 'SECONDARIES',
  [SortByType.CONVERSION]: 'CONVERSIONS',
  [SortByType.NEW_POOL]: 'POOLS',
  [SortByType.NEW_PLAN]: 'PLANS',
  [SortByType.SAFE]: 'SAFES',
  [SortByType.SAFE_CONVERSION]: 'SAFE_CONVERSIONS',
};

export const sortByTypeMap = Object.entries(sortByTypeTitle);

export enum SortByDate {
  ALL_TIME = 'all-time',
  CUSTOM_DATE = 'custom-date',
  LAST_30_DAYS = 'last-30-days',
  LAST_60_DAYS = 'last-60-days',
  LAST_QUARTER = 'last-quatre',
  LAST_YEAR = 'last-year',
}

export const sortByDateTitle: Record<SortByDate, ReactNode> = {
  [SortByDate.LAST_30_DAYS]: <AppFormattedMessage id="LAST_COUNT_DAYS" values={{ count: 30 }} />,
  [SortByDate.LAST_60_DAYS]: <AppFormattedMessage id="LAST_COUNT_DAYS" values={{ count: 60 }} />,
  [SortByDate.LAST_QUARTER]: <AppFormattedMessage id="LAST_QUARTER" />,
  [SortByDate.LAST_YEAR]: <AppFormattedMessage id="LAST_YEAR" />,
  [SortByDate.ALL_TIME]: <AppFormattedMessage id="ALL_TIME" />,
  [SortByDate.CUSTOM_DATE]: <AppFormattedMessage id="CUSTOM_DATE" />,
};

export const sortByDateMap = Object.entries(sortByDateTitle);

export enum EventExercisedTypeBackend {
  PHANTOM = 'phantom',
  PHANTOM_TERMINATED = 'phantom-terminated',
  RSA = 'rsa',
  RSA_TERMINATED = 'rsa-terminated',
  SAR = 'sar',
  SAR_TERMINATED = 'sar-terminated',
  STOCK_OPTIONS = 'stock-options',
  STOCK_OPTIONS_TERMINATED = 'stock-options-terminated',
  WARRANTS = 'warrants',
  WARRANTS_TERMINATED = 'warrants-terminated',
}

export enum EventTypeBackend {
  APPROVAL = 'approval',
  BUYBACK = 'buyback',
  COMPANY_INCORPORATION = 'company-incorporation',
  CONVERSION = 'conversion',
  EXERCISE = 'exercise',
  EXPIRE = 'expire',
  FUNDRAISING_ROUND = 'fundraising-round',
  GRANT = 'grant',
  NEW_PLAN = 'new-plan',
  NEW_POOL = 'new-pool',
  PAYOUT = 'payout',
  POOL_DECREASE = 'pool-decrease',
  POOL_INCREASE = 'pool-increase',
  SAFE = 'safe',
  SAFE_CONVERSION = 'safe-conversion',
  SECONDARIES = 'secondaries',
  SHARE_ISSUANCE = 'share-issuance',
  VALUATION = 'valuation',
}

/** Event types listed under the "Pools" history tab: creation plus formal size adjustments. */
export const POOL_EVENT_TYPES: readonly EventTypeBackend[] = [
  EventTypeBackend.NEW_POOL,
  EventTypeBackend.POOL_INCREASE,
  EventTypeBackend.POOL_DECREASE,
];

export enum EventFormType {
  APPROVAL = 'approval',
  BULK_IMPORT_GRANT = 'bulk-import-grant',
  BUYBACK = 'buyback',
  COMPANY_INCORPORATION = 'company-incorporation',
  CONVERSION = 'conversion',
  EXERCISE = 'exercise',
  FUNDRAISING_ROUND = 'fundraising-round',
  GRANT = 'grant',
  SAFE = 'safe',
  SAFE_CONVERSION = 'safe-conversion',
  SECONDARIES = 'secondaries',
  SHARE_ISSUANCE = 'share-issuance',
  VALUATION = 'valuation',
}

export const eventFormType: Record<EventTypeBackend, StringKey> = {
  [EventTypeBackend.GRANT]: 'GRANT',
  [EventTypeBackend.BUYBACK]: 'BUYBACK',
  [EventTypeBackend.CONVERSION]: 'CLASS_CONVERSION',
  [EventTypeBackend.FUNDRAISING_ROUND]: 'FUNDRAISING_ROUND',
  [EventTypeBackend.SECONDARIES]: 'SECONDARIES',
  [EventTypeBackend.SHARE_ISSUANCE]: 'SHARE_ISSUANCE',
  [EventTypeBackend.VALUATION]: 'VALUATION_EVENT',
  [EventTypeBackend.NEW_PLAN]: 'NEW_PLAN',
  [EventTypeBackend.NEW_POOL]: 'NEW_POOL',
  [EventTypeBackend.POOL_INCREASE]: 'POOL_INCREASE',
  [EventTypeBackend.POOL_DECREASE]: 'POOL_DECREASE',
  [EventTypeBackend.EXERCISE]: 'EXERCISED',
  [EventTypeBackend.APPROVAL]: 'APPROVAL',
  [EventTypeBackend.PAYOUT]: 'PAYOUT',
  [EventTypeBackend.EXPIRE]: 'EXPIRE',
  [EventTypeBackend.SAFE_CONVERSION]: 'SAFE_CONVERSION',
  [EventTypeBackend.SAFE]: 'SAFE',
  [EventTypeBackend.COMPANY_INCORPORATION]: 'COMPANY_INCORPORATION',
};

export const eventTypeMapping: Record<EventFormType, EventTypeBackend> = {
  [EventFormType.BULK_IMPORT_GRANT]: EventTypeBackend.GRANT,
  [EventFormType.GRANT]: EventTypeBackend.GRANT,
  [EventFormType.APPROVAL]: EventTypeBackend.APPROVAL,
  [EventFormType.EXERCISE]: EventTypeBackend.EXERCISE,
  [EventFormType.FUNDRAISING_ROUND]: EventTypeBackend.FUNDRAISING_ROUND,
  [EventFormType.SHARE_ISSUANCE]: EventTypeBackend.SHARE_ISSUANCE,
  [EventFormType.SECONDARIES]: EventTypeBackend.SECONDARIES,
  [EventFormType.BUYBACK]: EventTypeBackend.BUYBACK,
  [EventFormType.CONVERSION]: EventTypeBackend.CONVERSION,
  [EventFormType.VALUATION]: EventTypeBackend.VALUATION,
  [EventFormType.SAFE_CONVERSION]: EventTypeBackend.SAFE_CONVERSION,
  [EventFormType.SAFE]: EventTypeBackend.SAFE,
  [EventFormType.COMPANY_INCORPORATION]: EventTypeBackend.COMPANY_INCORPORATION,
};

export const sortByToEventFormTypeMap: Partial<Record<SortByType, EventFormType>> = {
  [SortByType.APPROVAL]: EventFormType.APPROVAL,
  [SortByType.EXERCISE]: EventFormType.EXERCISE,
  [SortByType.BUYBACK]: EventFormType.BUYBACK,
  [SortByType.CONVERSION]: EventFormType.CONVERSION,
  [SortByType.FUNDRAISING_ROUND]: EventFormType.FUNDRAISING_ROUND,
  [SortByType.GRANT]: EventFormType.GRANT,
  [SortByType.SAFE]: EventFormType.SAFE,
  [SortByType.SAFE_CONVERSION]: EventFormType.SAFE_CONVERSION,
  [SortByType.SECONDARIES]: EventFormType.SECONDARIES,
  [SortByType.SHARE_ISSUANCE]: EventFormType.SHARE_ISSUANCE,
  [SortByType.VALUATION]: EventFormType.VALUATION,
  [SortByType.COMPANY_INCORPORATION]: EventFormType.COMPANY_INCORPORATION,
};

export const backendToFormTypeMapping: Record<EventTypeBackend, EventFormType> = Object.fromEntries(
  Object.entries(eventTypeMapping).map(([formType, backendType]) => [backendType, formType]),
) as Record<EventTypeBackend, EventFormType>;

export const EventTypeTitle: Record<EventFormType, ReactNode> = {
  [EventFormType.APPROVAL]: <AppFormattedMessage id="APPROVAL" />,
  [EventFormType.EXERCISE]: <AppFormattedMessage id="EXERCISED" />,
  [EventFormType.GRANT]: <AppFormattedMessage id="ADD_GRANT" />,
  [EventFormType.BULK_IMPORT_GRANT]: <AppFormattedMessage id="BULK_IMPORT_GRANT" />,
  [EventFormType.COMPANY_INCORPORATION]: <AppFormattedMessage id="COMPANY_INCORPORATION" />,
  [EventFormType.FUNDRAISING_ROUND]: <AppFormattedMessage id="ADD_FUNDRAISING_ROUND" />,
  [EventFormType.SHARE_ISSUANCE]: <AppFormattedMessage id="ADD_SHARE_ISSUANCE" />,
  [EventFormType.SECONDARIES]: <AppFormattedMessage id="ADD_SECONDARIES" />,
  [EventFormType.BUYBACK]: <AppFormattedMessage id="ADD_BUYBACK" />,
  [EventFormType.CONVERSION]: <AppFormattedMessage id="ADD_CLASS_CONVERSION" />,
  [EventFormType.VALUATION]: <AppFormattedMessage id="ADD_VALUATION" />,
  [EventFormType.SAFE_CONVERSION]: <AppFormattedMessage id="SAFE_CONVERSION" />,
  [EventFormType.SAFE]: <AppFormattedMessage id="SAFE" />,
};

export const EventTypeButtonTitle: Record<EventFormType, ReactNode> = {
  [EventFormType.APPROVAL]: <AppFormattedMessage id="APPROVAL" />,
  [EventFormType.EXERCISE]: <AppFormattedMessage id="EXERCISED" />,
  [EventFormType.GRANT]: <AppFormattedMessage id="GRANT" />,
  [EventFormType.BULK_IMPORT_GRANT]: <AppFormattedMessage id="BULK_IMPORT_GRANT" />,
  [EventFormType.COMPANY_INCORPORATION]: <AppFormattedMessage id="COMPANY_INCORPORATION" />,
  [EventFormType.FUNDRAISING_ROUND]: <AppFormattedMessage id="FUNDRAISING_ROUND" />,
  [EventFormType.SHARE_ISSUANCE]: <AppFormattedMessage id="SHARES_ISSUANCE" />,
  [EventFormType.SECONDARIES]: <AppFormattedMessage id="SECONDARIES" />,
  [EventFormType.BUYBACK]: <AppFormattedMessage id="BUYBACK_DECREASE" />,
  [EventFormType.CONVERSION]: <AppFormattedMessage id="CLASS_CONVERSION" />,
  [EventFormType.VALUATION]: <AppFormattedMessage id="VALUATION" />,
  [EventFormType.SAFE_CONVERSION]: <AppFormattedMessage id="SAFE_CONVERSION" />,
  [EventFormType.SAFE]: <AppFormattedMessage id="SAFE" />,
};

export const EventTypeTitleString: Record<EventFormType, StringKey> = {
  [EventFormType.APPROVAL]: 'APPROVAL',
  [EventFormType.EXERCISE]: 'EXERCISED',
  [EventFormType.GRANT]: 'GRANT',
  [EventFormType.BULK_IMPORT_GRANT]: 'BULK_IMPORT_GRANT',
  [EventFormType.COMPANY_INCORPORATION]: 'COMPANY_INCORPORATION',
  [EventFormType.FUNDRAISING_ROUND]: 'FUNDRAISING_ROUND',
  [EventFormType.SHARE_ISSUANCE]: 'SHARES_ISSUANCE',
  [EventFormType.SECONDARIES]: 'SECONDARIES',
  [EventFormType.BUYBACK]: 'BUYBACK_DECREASE',
  [EventFormType.CONVERSION]: 'CLASS_CONVERSION',
  [EventFormType.VALUATION]: 'VALUATION',
  [EventFormType.SAFE_CONVERSION]: 'SAFE_CONVERSION',
  [EventFormType.SAFE]: 'SAFE_CONVERSION',
};

export enum ValuationSelect {
  PRE_MONEY_VALUATION = 'pre-money-valuation',
  SHARE_PRICE = 'share-price',
}

export const ValuationSelectTitle: Record<ValuationSelect, StringKey> = {
  [ValuationSelect.PRE_MONEY_VALUATION]: 'PRE_MONEY_VALUATION',
  [ValuationSelect.SHARE_PRICE]: 'SHARES_PRICE',
};

export type CreateGrantItemDto = {
  date: string;
  filesLinks: string[];
  id?: string;
  numbersOfGrants: number;
  planId: string;
  stakeholderId: string;
};

// `id` is optional on every item DTO: the backend edit path recreates the event, and reusing the
// existing item id is what preserves signatures and document links across that recreate.
export type CreateInvestmentItemDto = {
  date: string;
  filesLinks: string[];
  id?: string;
  investedValue: number;
  shareClassId: string;
  stakeholderId: string;
};

export type CreateBuybackItemDto = {
  date: string;
  filesLinks: string[];
  id?: string;
  shareClassId: string;
  sharesCount: number;
  stakeholderId: string;
};

export type CreateIssuanceEventItemDto = {
  date: string;
  filesLinks?: string[];
  id?: string;
  reverseVesting?: ReverseVestingSettings;
  shareClassId: string;
  sharesCount: number;
  stakeholderId: string;
};

export type CreateSecondariesEventItemDto = {
  date: string;
  filesLinks: string[];
  fromStakeholderId: string;
  id?: string;
  shareClassId: string;
  sharesCount: number;
  sharesValue: number;
  toStakeholderId: string;
};

export type CreateConversionItemDto = {
  date: string;
  fromShareClassId: string;
  id?: string;
  sharesCount: number;
  stakeholderId: string;
  toShareClassId: string;
};

export type CreateCompanyIncorporationItemDto = {
  filesLinks?: string[];
  id?: string;
  reverseVesting?: ReverseVestingSettings;
  shareClassId: string;
  sharesCount: number;
  stakeholderId: string;
};

export type CreateCompanyIncorporationEventDto = {
  date: string;
  items: CreateCompanyIncorporationItemDto[];
  parValue: string;
};

export type CreateValuationEventDto = {
  date: string;
  method?: ValuationSelect;
  name: string;
  sharePrice: number;
};

export type CreateEventDto = {
  additionalNotes: string;
  buyback?: {
    items: CreateBuybackItemDto[];
  };
  companyIncorporation?: CreateCompanyIncorporationEventDto;
  conversion?: {
    items: CreateConversionItemDto[];
  };
  filesLinks: string[];
  fundraisingRound?: {
    closeDate?: null | string;
    date: string;
    isOpen?: boolean;
    items: CreateInvestmentItemDto[];
    roundingSetting?: InvestmentRoundingSetting;
    sharePrice: number;
    // Exactly one of valuationId / valuation: inline valuation is created by the backend in the
    // same mutation (the async create response carries no valuation id to chain on).
    valuation?: CreateValuationEventDto;
    valuationId?: string;
  };
  grant?: {
    items: CreateGrantItemDto[];
    selectedTemplateId?: string;
    signatoryAssignments?: Record<string, { id: string }>;
  };
  issuance?: {
    date: string;
    items: CreateIssuanceEventItemDto[];
    sharePrice: number;
    // Same valuationId / valuation contract as fundraisingRound above.
    valuation?: CreateValuationEventDto;
    valuationId?: string;
  };
  secondaries?: {
    items: CreateSecondariesEventItemDto[];
  };
  type: EventTypeBackend;
  valuation?: CreateValuationEventDto;
};

export type Valuation = {
  createdAt: Date;
  date: Date;
  event: EventUnion;
  id: string;
  issuedShares?: number;
  issuedSharesOnStart?: number;
  name: string;
  sharePrice: number;
};

export type CreateEventResponse = {
  id: string;
  status?: EventLifecycleStatus;
  valuation?: Pick<Valuation, 'id'>;
};

export type EventSignatureSummary = {
  linkedSignatureFileLink?: string;
  signatureSignedCount: number;
  signatureStatus: string;
  signatureTotalCount: number;
};

// Async mutation lifecycle mirrored from the backend event-lifecycle-status.enum.ts (same
// string values). Drives the optimistic UI: every pending status (create, edit, delete) ->
// skeleton row, FAILED -> error + retry.
export enum EventLifecycleStatus {
  ACTIVE = 'active',
  FAILED = 'failed',
  PENDING_CREATE = 'pending-create',
  PENDING_DELETE = 'pending-delete',
  PENDING_EDIT = 'pending-edit',
}

export const isPendingLifecycleStatus = (status?: EventLifecycleStatus): boolean =>
  status === EventLifecycleStatus.PENDING_CREATE ||
  status === EventLifecycleStatus.PENDING_EDIT ||
  status === EventLifecycleStatus.PENDING_DELETE;

export interface CommonEvent {
  additionalNotes: string;
  createdAt: Date;
  date: Date;
  docusignFileLinks?: string[];
  eventId: string;
  filesLinks: string[];
  id: string;
  lastQueueError?: null | string;
  lifecycleStatus?: EventLifecycleStatus;
  pinned: boolean;
  signatureSummary?: EventSignatureSummary;
  updatedAt: Date;
}

export interface NewExerciseEvent extends CommonEvent {
  exercise: { id: string; items: ExerciseEventItem[] };
  type:
    | EventTypeBackend.APPROVAL
    | EventTypeBackend.EXERCISE
    | EventTypeBackend.EXPIRE
    | EventTypeBackend.PAYOUT;
}

export type TransactionSignatureFields = {
  docusignFileLinks?: string[];
  linkedSignatureFileLink?: string;
  linkedSignatureId?: string;
  signatureSignedCount?: number;
  signatureStatus?: string;
  signatureTotalCount?: number;
};

export interface GrantEventItem extends TransactionSignatureFields {
  date: Date;
  filesLinks: string[];
  id: string;
  inCurrentRound: boolean;
  numbersOfGrants: number;
  plan: SharePlan;
  processed: boolean;
  stakeholder: Stakeholder;
}

export interface ExerciseEventItem extends TransactionSignatureFields {
  date: Date;
  exercised: number;
  exerciseEvent: { event: NewExerciseEvent };
  filesLinks?: string[];
  id: string;
  payout?: number;
  returnedToPool?: number;
  vestingTask: VestingTask;
}

export interface FundraisingEventItem extends TransactionSignatureFields {
  date: Date;
  filesLinks: string[];
  fundraisingRound?: {
    roundingSetting?: InvestmentRoundingSetting;
    sharePrice?: number;
    valuation: { issuedShares: number; issuedSharesOnStart: number; name: string };
  };
  id: string;
  inCurrentRound: boolean;
  investedValue: number;
  shareClass: ShareClass;
  stakeholder: Stakeholder;
}

export interface GrantEvent extends CommonEvent {
  filesLinks: string[];
  hasSignatures?: boolean;
  items: GrantEventItem[];
  selectedTemplateId?: string;
  signatoryAssignments?: Record<string, { fullName?: string; id: string } | null>;
  type: EventTypeBackend.GRANT;
}

export interface FundraisingEvent extends CommonEvent {
  // `date` is the opening date and stays put when the round closes; `closeDate` is null while open.
  closeDate: Date | null;
  date: Date;
  isOpen: boolean;
  items: FundraisingEventItem[];
  roundingSetting: InvestmentRoundingSetting;
  sharePrice: number;
  type: EventTypeBackend.FUNDRAISING_ROUND;
  valuation: Valuation;
}

export interface ShareIssuanceEventItem extends TransactionSignatureFields {
  date: Date;
  filesLinks: string[];
  id: string;
  inCurrentRound: boolean;
  issuance?: {
    valuation: { issuedShares: number; issuedSharesOnStart: number; name: string };
  };
  reverseVesting?: ReverseVestingSettings;
  shareClass: ShareClass;
  sharesCount: number;
  stakeholder: Stakeholder;
}

export interface ShareIssuanceEvent extends CommonEvent {
  date: Date;
  items: ShareIssuanceEventItem[];
  sharePrice: number;
  type: EventTypeBackend.SHARE_ISSUANCE;
  valuation: Valuation;
}

export interface BuybackEventItem extends TransactionSignatureFields {
  date: Date;
  filesLinks: string[];
  id: string;
  inCurrentRound: boolean;
  shareClass: ShareClass;
  sharesCount: number;
  stakeholder: Stakeholder;
}

export interface BuybackEvent extends CommonEvent {
  items: BuybackEventItem[];
  type: EventTypeBackend.BUYBACK;
}

export interface SecondariesEventItem extends TransactionSignatureFields {
  date: Date;
  filesLinks: string[];
  fromStakeholder: Stakeholder;
  id: string;
  inCurrentRound: boolean;
  shareClass: ShareClass;
  sharesCount: number;
  sharesValue: number;
  toStakeholder: Stakeholder;
}

export interface SecondariesEvent extends CommonEvent {
  items: SecondariesEventItem[];
  type: EventTypeBackend.SECONDARIES;
}

export interface ConversionEventItem {
  date: Date;
  fromShareClass: ShareClass;
  id: string;
  inCurrentRound: boolean;
  sharesCount: number;
  stakeholder: Stakeholder;
  toShareClass: ShareClass;
}

export interface ConversionEvent extends CommonEvent {
  items: ConversionEventItem[];
  type: EventTypeBackend.CONVERSION;
}

export interface CompanyIncorporationEventItem extends TransactionSignatureFields {
  date: Date;
  filesLinks: string[];
  id: string;
  inCurrentRound: boolean;
  reverseVesting?: ReverseVestingSettings;
  shareClass: ShareClass;
  sharesCount: number;
  stakeholder: Stakeholder;
}

export interface CompanyIncorporationEvent extends CommonEvent {
  date: Date;
  filesLinks: string[];
  items: CompanyIncorporationEventItem[];
  parValue: string;
  type: EventTypeBackend.COMPANY_INCORPORATION;
}

export interface ValuationEvent extends CommonEvent {
  date: Date;
  issuedShares: number;
  issuedSharesOnStart: number;
  method: ValuationSelect;
  name: string;
  sharePrice: number;
  type: EventTypeBackend.VALUATION;
}

export interface NewPoolEvent extends CommonEvent {
  /**
   * Pool size at creation, set by the backend on list, detail and transaction responses: formal
   * adjustments move `pool.dillutedSharesCount` afterwards.
   */
  initialDilutedSharesCount?: number;
  pool: Pool;
  type: EventTypeBackend.NEW_POOL;
}

export type PoolAdjustment = {
  createdAt: Date;
  date: Date;
  id: string;
  newDilutedSharesCount: number;
  previousDilutedSharesCount: number;
  sharesCount: number;
};

export interface PoolAdjustmentEvent extends CommonEvent {
  pool: Pool;
  poolAdjustment: PoolAdjustment;
  type: EventTypeBackend.POOL_DECREASE | EventTypeBackend.POOL_INCREASE;
}

export interface NewPlanEvent extends CommonEvent {
  pool: Pool;
  sharePlan: SharePlan;
  type: EventTypeBackend.NEW_PLAN;
}

export type SafeConversion = {
  date: Date;
  event: Event;
  id: string;
  items: SafeWithPermissions[];
  sharePrice: number;
  valuation: Valuation;
};

export type SafeEvent = {
  type: EventTypeBackend.SAFE;
} & CommonEvent &
  OmitType<Safe, Function>;

export interface SafeConversionEvent extends CommonEvent {
  conversion: SafeConversion;
  type: EventTypeBackend.SAFE_CONVERSION;
}

export type EventUnionWithPermissions = WithPermissions<EventUnion>;

export type EventUnion =
  | BuybackEvent
  | CompanyIncorporationEvent
  | ConversionEvent
  | FundraisingEvent
  | GrantEvent
  | NewExerciseEvent
  | NewPlanEvent
  | NewPoolEvent
  | PoolAdjustmentEvent
  | SafeConversionEvent
  | SafeEvent
  | SecondariesEvent
  | ShareIssuanceEvent
  | ValuationEvent;

/**
 * A dividend payout served inside the events timeline ("All" tab). It is not an `Event`, so it
 * carries its own shape and is rendered read-only — payouts are managed on the Dividends page.
 */
export interface DividendTimelineEvent {
  approvalDate: null | string;
  canDelete: false;
  canEdit: false;
  createdAt: string;
  eventId: string;
  frequency: DividendFrequency;
  id: string;
  name: string;
  payoutDate: null | string;
  periodIndex: null | number;
  status: DividendPayoutStatus;
  totalAmount: number;
  transactionsCount: number;
  type: SortByType.DIVIDEND;
  year: number;
}

export type EventTimelineEntry = DividendTimelineEvent | EventUnionWithPermissions;

export const isDividendTimelineEvent = (
  entry: EventTimelineEntry,
): entry is DividendTimelineEvent => entry.type === SortByType.DIVIDEND;
